import { supabase } from '../../shared/supabase/supabaseClient';
import {
  REPORTS_REMOTE_CACHE_MS,
  reportsCacheKey,
  withReportsCache,
} from './reportsRemoteCache';
import { mapRpcError } from './reportsRemoteShared';
import type { BranchStockMermaByProductResponse, BranchStockMermaRow } from '../domain/reportsTypes';

/** Merma / compra / venta por producto (Informes > Operación). */
export async function fetchRemoteBranchStockMermaByProduct(params: {
  branchId: string;
  fromIso: string;
  toIso: string;
  limit?: number;
  offset?: number;
  uomFilter?: string | null;
}): Promise<BranchStockMermaByProductResponse | null> {
  const { branchId, fromIso, toIso, limit = 50, offset = 0, uomFilter = null } = params;
  return withReportsCache(
    reportsCacheKey([
      'reports_branch_stock_merma_by_product',
      branchId,
      fromIso,
      toIso,
      limit,
      offset,
      uomFilter ?? '',
    ]),
    async () => {
      const { data, error } = await supabase.rpc('reports_branch_stock_merma_by_product', {
        p_branch_id: branchId,
        p_from: fromIso,
        p_to: toIso,
        p_limit: limit,
        p_offset: offset,
        p_uom_filter: uomFilter,
      });
      if (error) {
        console.warn('[reportsRemote] reports_branch_stock_merma_by_product', mapRpcError(error));
        return null;
      }
      if (!data || typeof data !== 'object') return null;
      const j = data as Record<string, unknown>;
      const rawItems = j.items;
      const items: BranchStockMermaRow[] = [];
      if (Array.isArray(rawItems)) {
        for (const raw of rawItems) {
          if (!raw || typeof raw !== 'object') continue;
          const r = raw as Record<string, unknown>;
          items.push({
            productId: String(r.product_id ?? ''),
            label: String(r.label ?? ''),
            unitOfMeasure: r.unit_of_measure == null ? null : String(r.unit_of_measure),
            purchased: Number(r.purchased ?? 0),
            sold: Number(r.sold ?? 0),
            shrinkage: Number(r.shrinkage ?? 0),
            breakage: Number(r.breakage ?? 0),
            expiry: Number(r.expiry ?? 0),
            expiryCost: Number(r.expiry_cost ?? 0),
            shrinkageCost: Number(r.shrinkage_cost ?? 0),
            netGap: Number(r.net_gap ?? 0),
          });
        }
      }
      return {
        items,
        totalCount: Number(j.total_count ?? 0),
        sumPurchased: Number(j.sum_purchased ?? 0),
        sumSold: Number(j.sum_sold ?? 0),
        sumShrinkage: Number(j.sum_shrinkage ?? 0),
        sumBreakage: Number(j.sum_breakage ?? 0),
        sumExpiry: Number(j.sum_expiry ?? 0),
        sumExpiryCost: Number(j.sum_expiry_cost ?? 0),
        sumShrinkageCost: Number(j.sum_shrinkage_cost ?? 0),
      };
    },
    REPORTS_REMOTE_CACHE_MS,
    { category: 'reports', name: 'reports_branch_stock_merma_by_product' },
  );
}
